import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useReduxState } from 'hooks';
import { getCustomizedNavTitle } from 'lib/helpers';
import { filterValuesSeparator } from 'shared-helpers/consts';
import { Search } from 'components/common';
import { PageContent, PageHeader, PageSectionHeader } from 'components/page';
import {
  PathsCreate,
  PathsNav,
  PathsViewAdministered,
  PathsViewAllPaths,
  PathsViewAvailable,
  PathsViewUser
} from 'components/paths';

import type { GetPathsRes } from 'shared-types/responses';

const Paths = () => {
  const {
    language,
    messages,
    serverConfig,
    user
  } = useReduxState();

  const hasCreatePermissions =
    user?.roles.includes('superadmin') ||
    user?.permissions.includes('create-paths');

  const router = useRouter();

  const [previousView, setPreviousView] = useState<string>();
  const [filterData, setFilterData] = useState<
    GetPathsRes['pathsFilterOptions'] | null
  >(null);

  const type = router.query.type as string;

  useEffect(() => {
    if (!type) pushToRoute();
  }, [type]);

  const pushToRoute = (route?: string) => {
    const type = route ? route : 'all';
    router.replace(`/paths?type=${type}`, `/paths?type=${type}`, {
      shallow: true
    });
  };

  const handleSearch = ({ search }: { search: string }) => {
    if (search) {
      const type = router.query.type as string;
      if (type !== 'search') setPreviousView(type);

      router.push(
        `/paths?type=search&search=${search}`,
        `/paths?type=search&search=${search}`,
        { shallow: true }
      );
    } else pushToRoute(previousView);
  };

  const queryParams = Object.keys(router.query)
    .filter((param) => param !== 'type')
    .map((param) => {
      const value = router.query[param];
      return Array.isArray(value)
        ? `&${param}=${encodeURIComponent(value.join(filterValuesSeparator))}`
        : `&${param}=${encodeURIComponent(value)}`;
    })
    .join('');

  return (
    <div className="paths">
      <PageHeader
        icon="tasks"
        title={getCustomizedNavTitle({
          defaultTitle: messages.paths,
          navLinksCustomTexts: serverConfig.navLinksCustomTexts,
          language,
          section: 'paths'
        })}
      >
        <div className="paths-header">
          <div className="search">
            <Search
              onSearch={handleSearch}
              placeholder={messages.pathsSearch}
              size="medium"
            />
          </div>

          {hasCreatePermissions ? <PathsCreate /> : null}
        </div>
      </PageHeader>

      <PageContent withSidebar={true}>
        <PathsNav categories={filterData?.categories} />

        <div className="content">
          {type === 'administered' ? (
            <PathsViewAdministered
              onFilterDataReceived={setFilterData}
              queryParams={queryParams}
            />
          ) : type === 'active' ? (
            <PathsViewUser
              onFilterDataReceived={setFilterData}
              queryParams={queryParams}
            />
          ) : type === 'available' ? (
            <PathsViewAvailable
              onFilterDataReceived={setFilterData}
              queryParams={queryParams}
            />
          ) : type === 'all' ? (
            <PathsViewAllPaths
              onFilterDataReceived={setFilterData}
              queryParams={queryParams}
            />
          ) : type === 'search' ? (
            <>
              <PageSectionHeader
                title={`${messages.commonSearch}: ${router.query.search}`}
              />

              <PathsViewAllPaths
                onFilterDataReceived={setFilterData}
                queryParams={queryParams}
              />
            </>
          ) : null}
        </div>
      </PageContent>

      <style jsx>{`
        .paths-header {
          align-items: center;
          display: flex;
          flex-wrap: wrap;
          gap: 20px;
        }
        .search {
          width: 320px;
        }
        .content {
          flex-grow: 1;
        }
      `}</style>
    </div>
  );
};

export default Paths;
